import React from "react";
import { createRoot } from "react-dom/client";
import Dropdown from "../../components/buttons/Dropdown";
import { extractIdFromIri } from "../../utils/extractFromIri";
import {
  extractCollectionItems,
  extractPaginatedCollection,
  parseApiPayload,
} from "../../utils/apiResponse";
import { runtimeEnv } from "../../utils/runtimeEnv";

const PARTICIPANT_MANAGEMENT_TAB_STATUSES = {
  ACTIVE: new Set(["active", "suspended"]),
  INACTIVE: new Set(["revoked", "withdrawn"]),
};

const PARTICIPANT_TECHNICAL_DATA_FIELDS = [
  "workerPort",
  "connectorHost",
  "ssoo",
];

const PARTICIPANT_TECHNICAL_DATA_EDITABLE_FIELDS = [
  {
    key: "connectorHost",
    inputType: "text",
  },
  {
    key: "workerPort",
    inputType: "number",
    localOnly: true,
  },
  {
    key: "ssoo",
    inputType: "text",
    readOnly: true,
  },
];

const WORKER_PORT_ENVIRONMENTS = new Set(["local", "development"]);

export function shouldUseParticipantWorkerPort(env = runtimeEnv) {
  return WORKER_PORT_ENVIRONMENTS.has(
    String(env?.REACT_APP_ENV ?? "")
      .trim()
      .toLowerCase(),
  );
}

function getParticipantTechnicalDataEditableFields() {
  const includeWorkerPort = shouldUseParticipantWorkerPort();

  return PARTICIPANT_TECHNICAL_DATA_EDITABLE_FIELDS.filter(
    (field) => !field.localOnly || includeWorkerPort,
  );
}

export const PARTICIPANT_TECHNICAL_DATA_ADD_CONNECTOR_CHECKBOX_ID =
  "participant-technical-data-add-connector";

export function normalizeWorkerResponse(res) {
  const data = res?.data ?? res;
  return data;
}

export function extractStatus(res) {
  return (
    res?.statusCode ??
    res?.status ??
    res?.data?.statusCode ??
    res?.data?.status ??
    undefined
  );
}

export function parseWorkerPayload(res) {
  const raw = normalizeWorkerResponse(res);
  return parseApiPayload(raw);
}

export function assertSuccessfulWorkerResponse(res, errorMessage) {
  const status = extractStatus(res);
  const data = normalizeWorkerResponse(res);

  if (status && status >= 400) {
    const error = new Error(`${errorMessage} (${status})`);
    error.statusCode = status;
    error.response = data;
    throw error;
  }

  return data;
}

export function parseParticipantsList(res) {
  return extractCollectionItems(parseWorkerPayload(res));
}

export function parseParticipantsPagination(res, fallbackLength = 0) {
  return extractPaginatedCollection(parseWorkerPayload(res), fallbackLength);
}

export function normalizeParticipantListItem(participant) {
  if (!participant || typeof participant !== "object") return participant;

  const identifierNotation =
    participant?.identifier?.[0]?.notation ??
    participant?.externalIdentifier ??
    "";

  const identifier =
    Array.isArray(participant?.identifier) && participant.identifier.length > 0
      ? participant.identifier
      : [
          {
            notation: identifierNotation,
          },
        ];

  return {
    ...participant,
    id: getParticipantId(participant) ?? crypto.randomUUID(),
    identifier,
    updated_at: participant?.updated_at ?? participant?.updatedAt ?? null,
    created_at: participant?.created_at ?? participant?.createdAt ?? null,
  };
}

export function parseSingleParticipant(res) {
  const container = parseWorkerPayload(res);

  const looksLikeParticipant =
    container &&
    typeof container === "object" &&
    !Array.isArray(container) &&
    (Array.isArray(container?.preferredLabel) ||
      Array.isArray(container?.identifier) ||
      container?.address ||
      container?.contactPoint);

  const maybeArr = extractCollectionItems(container);

  if (Array.isArray(maybeArr) && maybeArr.length > 0) return maybeArr[0];
  if (looksLikeParticipant) return container;
  return maybeArr ?? null;
}

export function getParticipantId(participant) {
  if (!participant || typeof participant !== "object") return participant;

  return (
    participant?.dsbParticipantId ??
    participant?.id ??
    participant?.participant_id ??
    extractIdFromIri(participant?.id) ??
    participant?.connector?.[0]?.identifier ??
    participant?.agent_id ??
    participant?.identifier?.[0]?.notation ??
    participant?.externalIdentifier ??
    null
  );
}

export function getParticipantLifecycleStatus(participant) {
  return String(
    participant?.status ?? participant?.connector?.[0]?.status ?? "",
  )
    .trim()
    .toLowerCase();
}

function getParticipantKeycloakProvisionStatus(participant) {
  return String(participant?.keycloakProvisionStatus ?? "")
    .trim()
    .toLowerCase();
}

function getParticipantTechnicalFormStatus(participant) {
  return String(participant?.technicalFormStatus ?? "")
    .trim()
    .toLowerCase();
}

export function getParticipantManagementActionKey(participant) {
  const status = getParticipantLifecycleStatus(participant);
  const keycloakProvisionStatus =
    getParticipantKeycloakProvisionStatus(participant);
  const technicalFormStatus = getParticipantTechnicalFormStatus(participant);

  if (status === "revoked" || status === "withdrawn") return "reactivate";
  if (status === "suspended" && keycloakProvisionStatus === "provisioned") {
    if (technicalFormStatus === "received") return "activate";
    return "technicalData";
  }
  if (status === "active" && keycloakProvisionStatus === "provisioned") {
    return "deactivate";
  }

  return "approval";
}

function getParticipantConnectorRole(participant) {
  return getParticipantConnectorRoles(participant)[0] ?? "";
}

export function getParticipantConnectorRoles(participant) {
  const roles = [
    ...(Array.isArray(participant?.roles) ? participant.roles : []),
    ...(Array.isArray(participant?.role) ? participant.role : []),
    ...(typeof participant?.roles === "string" ? [participant.roles] : []),
    ...(typeof participant?.role === "string" ? [participant.role] : []),
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return roles.filter(
    (role, index) =>
      (role === "data_provider" || role === "data_consumer") &&
      roles.indexOf(role) === index,
  );
}

function getParticipantTechnicalDataConfig(role) {
  if (role === "data_consumer") {
    return {
      workerPort: 18179,
      connectorHost: "test.nttdata-dataspacebuilder.com",
      ssoo: "data_consumer",
    };
  }

  if (role === "data_provider") {
    return {
      workerPort: 18180,
      connectorHost: "test.nttdata-dataspacebuilder.com",
      ssoo: "data_provider",
    };
  }

  return null;
}

function extractConnectorRequestId(connectorData) {
  const data = Array.isArray(connectorData) ? connectorData[0] : connectorData;

  const connectorRequest = data?.connectorRequest;

  if (typeof connectorRequest === "string") {
    return extractIdFromIri(connectorRequest) ?? connectorRequest;
  }

  return (
    connectorRequest?.id ??
    connectorRequest?.identifier ??
    connectorRequest?.connectorRequestId ??
    data?.connectorRequestId ??
    ""
  );
}

function normalizeConnectorDataEntries(connectorData) {
  const rawConnectorData = parseWorkerPayload(connectorData);

  if (Array.isArray(rawConnectorData)) return rawConnectorData;
  if (Array.isArray(connectorData)) return connectorData;

  const parsedConnectorData = parseSingleParticipant(connectorData);
  const data = parsedConnectorData ?? rawConnectorData ?? connectorData;

  if (Array.isArray(data)) return data.length ? data : [rawConnectorData];
  return data ? [data] : [];
}

function withoutInternalTechnicalDataFields(payload) {
  const { __suggestions, __actualFields, ...cleanPayload } = payload ?? {};
  return cleanPayload;
}

export function buildConnectorTechnicalDataBody(connectorData, fallback = {}) {
  const source = withoutInternalTechnicalDataFields(connectorData);
  const fallbackSource = withoutInternalTechnicalDataFields(fallback);
  const body = {};

  const connectorRequest =
    source?.connectorRequest ?? fallbackSource?.connectorRequest;
  const participant = source?.participant ?? fallbackSource?.participant;
  const participantName =
    source?.participantName ?? fallbackSource?.participantName;

  if (connectorRequest) {
    body.connectorRequest = {
      id: connectorRequest?.id ?? connectorRequest,
    };
  }

  if (participant) {
    body.participant = {
      dsbParticipantId:
        participant?.dsbParticipantId ??
        participant?.id ??
        fallbackSource?.participant?.dsbParticipantId ??
        "",
    };
  }

  if (participantName !== undefined) body.participantName = participantName;

  body.connectorHost =
    source?.connectorHost ?? fallbackSource?.connectorHost ?? null;
  body.ssoo = source?.ssoo ?? fallbackSource?.ssoo ?? null;

  if (shouldUseParticipantWorkerPort()) {
    body.workerPort = source?.workerPort ?? fallbackSource?.workerPort ?? null;
  }

  return body;
}

function buildSingleParticipantTechnicalDataPayload({
  participant,
  connectorData,
  participantName = "",
  role,
}) {
  const parsedSingleConnectorData = parseSingleParticipant(connectorData);
  const parsedConnectorData = Array.isArray(parsedSingleConnectorData)
    ? parseWorkerPayload(connectorData)
    : (parsedSingleConnectorData ?? parseWorkerPayload(connectorData));
  const rawConnectorData = parseWorkerPayload(connectorData);
  const connectorRequestId =
    extractConnectorRequestId(parsedConnectorData) ||
    extractConnectorRequestId(rawConnectorData) ||
    extractConnectorRequestId(connectorData);
  const sourceParticipant =
    parsedConnectorData?.participant ??
    rawConnectorData?.participant ??
    connectorData?.participant;

  const connectorRole =
    role ||
    getParticipantConnectorRole(participant) ||
    getParticipantConnectorRole(sourceParticipant);

  const config = getParticipantTechnicalDataConfig(connectorRole);

  if (!config) return null;

  const actualFields = [];
  const technicalDataOverrides = [
    parsedConnectorData,
    rawConnectorData,
    connectorData,
  ].reduce((acc, source) => {
    if (!source || typeof source !== "object") return acc;

    PARTICIPANT_TECHNICAL_DATA_FIELDS.forEach((field) => {
      if (source[field] !== undefined && source[field] !== null) {
        acc[field] = source[field];
        actualFields.push(field);
      }
    });

    return acc;
  }, {});

  const resolvedParticipantName =
    parsedConnectorData?.participantName ??
    rawConnectorData?.participantName ??
    connectorData?.participantName ??
    participantName ??
    "";

  if (resolvedParticipantName) actualFields.push("participantName");

  const payload = {
    connectorRequest: {
      id: connectorRequestId,
    },
    participant: {
      dsbParticipantId:
        sourceParticipant?.dsbParticipantId ??
        participant?.dsbParticipantId ??
        getParticipantId(participant) ??
        "",
    },
    participantName: resolvedParticipantName,
    ...config,
    ...technicalDataOverrides,
  };

  Object.defineProperties(payload, {
    __suggestions: {
      value: config,
      enumerable: false,
    },
    __actualFields: {
      value: [...new Set(actualFields)],
      enumerable: false,
    },
  });

  return payload;
}

export function buildParticipantTechnicalDataPayload({
  participant,
  connectorData,
  participantName = "",
}) {
  return (
    buildParticipantTechnicalDataPayloads({
      participant,
      connectorData,
      participantName,
    })[0] ?? null
  );
}

export function buildParticipantTechnicalDataPayloads({
  participant,
  connectorData,
  participantName = "",
}) {
  const connectorDataEntries = normalizeConnectorDataEntries(connectorData);
  const entries = connectorDataEntries.length ? connectorDataEntries : [{}];
  const participantRoles = getParticipantConnectorRoles(participant);

  return entries
    .map((entry, index) => {
      const role =
        participantRoles[index] ??
        getParticipantConnectorRole(entry?.participant) ??
        participantRoles[0];

      return buildSingleParticipantTechnicalDataPayload({
        participant,
        connectorData: entry,
        participantName,
        role,
      });
    })
    .filter(Boolean);
}

export function filterParticipantsByManagementTab(
  participants = [],
  activeTab,
) {
  const tabStatuses =
    PARTICIPANT_MANAGEMENT_TAB_STATUSES[activeTab] ??
    PARTICIPANT_MANAGEMENT_TAB_STATUSES.ACTIVE;

  return (Array.isArray(participants) ? participants : []).filter(
    (participant) =>
      tabStatuses.has(getParticipantLifecycleStatus(participant)),
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function buildParticipantApprovalRejectHtml(t) {
  return `
    <div class="participant-approval-form">
      <p class="participant-approval-help">
        ${escapeHtml(t("participant.approval.rejectReasonPrompt"))}
      </p>
      <label class="participant-approval-field">
        <span>${escapeHtml(t("participant.approval.reasonLabel"))}</span>
        <textarea
          id="participant-approval-reason"
          class="participant-approval-textarea"
          rows="3"
          placeholder="${escapeHtml(t("participant.approval.reasonPlaceholder"))}"
        ></textarea>
      </label>
      <label class="participant-approval-field">
        <span>${escapeHtml(t("participant.approval.commentLabel"))}</span>
        <textarea
          id="participant-approval-comment"
          class="participant-approval-textarea"
          rows="3"
          placeholder="${escapeHtml(t("participant.approval.commentPlaceholder"))}"
        ></textarea>
      </label>
    </div>
  `;
}

function getTechnicalDataInputId(key, index, isMultiple) {
  return isMultiple
    ? `participant-technical-data-${index}-${key}`
    : `participant-technical-data-${key}`;
}

function buildParticipantTechnicalDataFields(t, payload, index, isMultiple) {
  const actualFields = new Set(payload?.__actualFields ?? []);
  const suggestions = payload?.__suggestions ?? {};

  return getParticipantTechnicalDataEditableFields().map(
    ({ key, inputType, readOnly }) => {
      const hasActualValue = actualFields.has(key);
      const rawValue = readOnly
        ? (payload?.[key] ?? suggestions?.[key] ?? "")
        : hasActualValue
          ? payload?.[key]
          : "";
      const rawPlaceholder = !hasActualValue ? suggestions?.[key] : "";

      return {
        label: t(
          `participant.technicalDataDialog.fields.${key}`,
          key === "connectorId" ? "Connector ID" : key,
        ),
        value: rawValue,
        placeholder: rawPlaceholder,
        key,
        inputType,
        readOnly,
        id: getTechnicalDataInputId(key, index, isMultiple),
      };
    },
  );
}

export function buildParticipantTechnicalDataHtml(t, payload, options = {}) {
  const payloads = Array.isArray(payload) ? payload : [payload];
  const isMultiple = payloads.length > 1;
  const {
    canAddConnector = false,
    addConnectorChecked = false,
    addConnectorLoading = false,
  } = options;
  const addConnectorControl = canAddConnector
    ? `
      <label class="participant-approval-checkbox-field">
        <input
          id="${PARTICIPANT_TECHNICAL_DATA_ADD_CONNECTOR_CHECKBOX_ID}"
          class="participant-approval-checkbox"
          type="checkbox"
          ${addConnectorChecked ? "checked" : ""}
          ${addConnectorLoading ? "disabled" : ""}
        />
        <span>
          ${escapeHtml(
            t(
              "participant.technicalDataDialog.addConnector",
              "Add another connector form",
            ),
          )}
        </span>
      </label>
      ${
        addConnectorLoading
          ? `<p class="participant-approval-help">${escapeHtml(
              t(
                "participant.technicalDataDialog.addConnectorLoading",
                "Creating connector request...",
              ),
            )}</p>`
          : ""
      }
    `
    : "";

  return `
    <div class="participant-approval-form">
      <p class="participant-approval-help">
        ${escapeHtml(
          t(
            "participant.technicalDataDialog.prompt",
            "Review the technical data that will be sent for this participant.",
          ),
        )}
      </p>
      ${addConnectorControl}
      ${payloads
        .map((item, index) => {
          const fields = buildParticipantTechnicalDataFields(
            t,
            item,
            index,
            isMultiple,
          );
          const connectorType =
            item?.ssoo === "data_provider"
              ? t("roles.provider", "Provider")
              : item?.ssoo === "data_consumer"
                ? t("roles.consumer", "Consumer")
                : item?.ssoo;
          const title = [
            t("participant.technicalDataDialog.connectorTitle", "Connector"),
            connectorType,
          ]
            .filter(Boolean)
            .join(" ");
          const formGrid = `
            <div class="participant-approval-grid">
              ${fields
                .map(
                  ({ label, value, placeholder, id, inputType, readOnly }) => `
                    <label class="participant-approval-field">
                      <span>${escapeHtml(label)}</span>
                      <input
                        id="${escapeHtml(id)}"
                        class="participant-approval-input"
                        type="${inputType ?? "text"}"
                        ${inputType === "number" ? 'min="0" step="1"' : ""}
                        ${readOnly ? 'readonly aria-readonly="true"' : ""}
                        value="${escapeHtml(value ?? "")}"
                        placeholder="${escapeHtml(placeholder ?? "")}"
                      />
                    </label>
                  `,
                )
                .join("")}
            </div>
          `;

          return `
            <details class="participant-approval-accordion" ${
              index === 0 ? "open" : ""
            }>
              <summary class="participant-approval-accordion-summary">
                <span>${escapeHtml(title)}</span>
              </summary>
              <div class="participant-approval-accordion-content">
                ${formGrid}
              </div>
            </details>
          `;
        })
        .join("")}
    </div>
  `;
}

function getSingleParticipantTechnicalDataPayload(
  popup,
  payload,
  index,
  isMultiple,
) {
  const nextPayload = {
    ...buildConnectorTechnicalDataBody(payload),
  };

  for (const field of getParticipantTechnicalDataEditableFields()) {
    if (field.readOnly) continue;

    const input = popup?.querySelector(
      `#${getTechnicalDataInputId(field.key, index, isMultiple)}`,
    );
    const rawValue = String(input?.value ?? "").trim();

    if (!rawValue) {
      return {
        payload: null,
        invalidFieldKey: field.key,
      };
    }

    if (field.inputType === "number") {
      const parsedValue = Number(rawValue);

      if (!Number.isInteger(parsedValue) || parsedValue < 0) {
        return {
          payload: null,
          invalidFieldKey: field.key,
        };
      }

      nextPayload[field.key] = parsedValue;
      continue;
    }

    nextPayload[field.key] = rawValue;
  }

  return {
    payload: nextPayload,
    invalidFieldKey: null,
  };
}

export function getParticipantTechnicalDataPayload(popup, payload) {
  const payloads = Array.isArray(payload) ? payload : [payload];
  const isMultiple = payloads.length > 1;
  const nextPayloads = [];

  for (let index = 0; index < payloads.length; index += 1) {
    const result = getSingleParticipantTechnicalDataPayload(
      popup,
      payloads[index],
      index,
      isMultiple,
    );

    if (!result.payload) {
      return {
        ...result,
        invalidPayloadIndex: index,
      };
    }

    nextPayloads.push(result.payload);
  }

  return {
    payload: isMultiple ? nextPayloads : nextPayloads[0],
    invalidFieldKey: null,
    ...(isMultiple ? { invalidPayloadIndex: null } : {}),
  };
}

function getKeycloakUserOrganization(user) {
  const organization = user?.attributes?.organization;

  if (Array.isArray(organization)) {
    return String(organization[0] ?? "").trim();
  }

  return String(organization ?? "").trim();
}

export function getKeycloakUserRoles(user) {
  const directRoles = Array.isArray(user?.roles)
    ? user.roles
    : Array.isArray(user?.role)
      ? user.role
      : Array.isArray(user?.realmRoles)
        ? user.realmRoles
        : Array.isArray(user?.attributes?.["userAttributes.roles"])
          ? user.attributes["userAttributes.roles"]
          : Array.isArray(user?.attributes?.userAttributes?.roles)
            ? user.attributes.userAttributes.roles
            : [];

  return directRoles.map((role) => String(role ?? "").trim()).filter(Boolean);
}

export function getKeycloakUserParticipantIds(user) {
  const participantIds = Array.isArray(user?.participant_id)
    ? user.participant_id
    : Array.isArray(user?.attributes?.participant_id)
      ? user.attributes.participant_id
      : user?.attributes?.participant_id
        ? [user.attributes.participant_id]
        : [];

  return participantIds
    .map((participantId) => String(participantId ?? "").trim())
    .filter(Boolean);
}

function getTranslatedKeycloakRoleLabel(t, role) {
  const normalizedRole = String(role ?? "").trim();

  if (!normalizedRole) return "";
  if (normalizedRole === "administrator") {
    return t("roles.admin", normalizedRole);
  }

  return t(`roles.${normalizedRole}`, normalizedRole);
}

function getParticipantApprovalUserOptions(t, users = []) {
  return users.map((user) => {
    const username = String(user?.username ?? "").trim();
    const fullName = [user?.firstName, user?.lastName]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
      .join(" ");
    const displayName = fullName || username;
    const organization = getKeycloakUserOrganization(user);
    const roles = getKeycloakUserRoles(user)
      .map((role) => getTranslatedKeycloakRoleLabel(t, role))
      .filter(Boolean);
    const details = [
      organization,
      roles.join(", "),
      username && fullName ? `@${username}` : "",
    ]
      .filter(Boolean)
      .join(" • ");

    return {
      value: String(user?.id ?? ""),
      label: details ? `${displayName} • ${details}` : displayName,
    };
  });
}

export function buildParticipantApprovalAcceptHtml(
  t,
  _users = [],
  defaultComment = "",
  promptKey = "participant.approval.acceptUserPrompt",
) {
  return `
    <div class="participant-approval-form">
      <p class="participant-approval-help">
        ${escapeHtml(
          t(
            promptKey,
            "Select the Keycloak user to link to this participant approval.",
          ),
        )}
      </p>
      <label class="participant-approval-field">
        <span>${escapeHtml(
          t("participant.approval.userLabel", "Keycloak user"),
        )}</span>
        <div
          id="participant-approval-user-autocomplete"
          class="participant-approval-autocomplete"
        ></div>
        <input id="participant-approval-user" type="hidden" value="" />
      </label>
      <label class="participant-approval-field">
        <span>${escapeHtml(t("participant.approval.commentLabel"))}</span>
        <textarea
          id="participant-approval-comment"
          class="participant-approval-textarea"
          rows="3"
          placeholder="${escapeHtml(t("participant.approval.commentPlaceholder"))}"
        >${escapeHtml(defaultComment)}</textarea>
      </label>
    </div>
  `;
}

export function mountParticipantApprovalUserAutocomplete(popup, t, users = []) {
  const mountNode = popup?.querySelector(
    "#participant-approval-user-autocomplete",
  );
  const hiddenInput = popup?.querySelector("#participant-approval-user");

  if (!mountNode || !hiddenInput) return null;

  const root = createRoot(mountNode);
  const options = getParticipantApprovalUserOptions(t, users);

  function ParticipantApprovalUserAutocomplete() {
    const [selectedValue, setSelectedValue] = React.useState(
      hiddenInput.value || "",
    );

    return (
      <Dropdown
        variant="autocomplete"
        id="participant-approval-user-autocomplete-input"
        value={selectedValue}
        onChange={(value) => {
          const normalizedValue = String(value ?? "");
          setSelectedValue(normalizedValue);
          hiddenInput.value = normalizedValue;
        }}
        options={options}
        placeholderKey="participant.approval.userPlaceholder"
        maxWidth={9999}
        sx={{ maxWidth: "100%" }}
        autocompleteProps={{
          disablePortal: true,
          openOnFocus: true,
        }}
      />
    );
  }

  root.render(<ParticipantApprovalUserAutocomplete />);

  popup.__participantApprovalAutocompleteRoot = root;
  return root;
}

export function unmountParticipantApprovalUserAutocomplete(popup) {
  popup?.__participantApprovalAutocompleteRoot?.unmount?.();
  if (popup) {
    delete popup.__participantApprovalAutocompleteRoot;
  }
}

export function getParticipantApprovalRejectPayload(popup) {
  const reason = popup
    ?.querySelector("#participant-approval-reason")
    ?.value?.trim();
  const comment = popup
    ?.querySelector("#participant-approval-comment")
    ?.value?.trim();

  return { reason, comment };
}

export function getParticipantApprovalAcceptPayload(popup, usersById) {
  const selectedUserId = popup
    ?.querySelector("#participant-approval-user")
    ?.value?.trim();
  const comment = popup
    ?.querySelector("#participant-approval-comment")
    ?.value?.trim();

  const selectedUser = selectedUserId ? usersById?.[selectedUserId] : null;

  if (!selectedUser) {
    return { selectedUserId, comment, keycloak: null };
  }

  const username = String(selectedUser.username ?? "").trim();
  const roles = getKeycloakUserRoles(selectedUser);

  return {
    selectedUserId,
    comment,
    keycloak: {
      clientId: `${username}_${roles[0]}`,
      organization: getKeycloakUserOrganization(selectedUser),
      username: String(selectedUser.username ?? "").trim(),
      roles: getKeycloakUserRoles(selectedUser),
    },
  };
}
